// Refactor 02/2026 — extrahiert aus AppointmentsPage.js (Phase 6, Iter 334).
// Bündelt alle Dialoge der Termine-Seite (Context-Menü, Edit, Delete,
// Booking, Overwrite-Warning, Availability, QuickCheckout, Anamnese,
// Pause, Series-Skipped) in einer Wrapper-Komponente.
//
// Reine Render-Komponente — kein Custom Logic, nur Props-Forwarding.

import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { BreakDialog } from './BreakDialog';
import { DeleteConfirmDialog } from './DeleteConfirmDialog';
import { DeleteOptionsDialog } from './DeleteOptionsDialog';
import { OverwriteWarningDialog } from './OverwriteWarningDialog';
import { AvailabilityDialog } from './AvailabilityDialog';
import { AnamneseDialog } from './AnamneseDialog';
import { EditAppointmentDialog } from './EditAppointmentDialog';
import { BookingDialog } from './BookingDialog';
import { AppointmentContextMenu } from './AppointmentContextMenu';
import AppointmentHistoryDialog from './AppointmentHistoryDialog';
import SeriesSkippedDialog from './SeriesSkippedDialog';
import QuickCheckoutDialog from '@/components/QuickCheckoutDialog';
import SellProductDialog from '@/components/customers/dialogs/SellProductDialog';
import { CustomFieldsQuickDialog } from './CustomFieldsQuickDialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AppointmentsDialogs = ({
  // Context Menu
  contextMenu,
  setContextMenu,
  selectedAppointment,
  services,
  trainers,
  anamneseStatus,
  onEditAppointment,
  onViewAnamnese,
  onCheckIn,
  onMoveAppointment,
  onMoveParticipant,
  onRemoveParticipant,
  onAvailableSlotClick,
  setDeleteOptionsDialogOpen,
  onManageSeries,
  onWhatsAppReschedule,
  menuConfig,
  // Edit
  editDialogOpen,
  setEditDialogOpen,
  customers,
  customTime,
  setCustomTime,
  bookingData,
  setBookingData,
  onEditSubmit,
  // Delete (Confirm)
  deleteDialogOpen,
  setDeleteDialogOpen,
  onConfirmDelete,
  // Delete Options
  deleteOptionsDialogOpen,
  onDeleteAppointment,
  // Booking
  bookingDialogOpen,
  setBookingDialogOpen,
  setIsAddingParticipant,
  setAvailableServices,
  selectedTrainer,
  selectedSlot,
  currentDate,
  availableServices,
  isAddingParticipant,
  isRecurring,
  setIsRecurring,
  recurringFrequency,
  setRecurringFrequency,
  recurringOccurrences,
  setRecurringOccurrences,
  notifyCustomer,
  setNotifyCustomer,
  isBooking,
  onBookingSubmit,
  // Overwrite Warning
  overwriteWarningDialogOpen,
  setOverwriteWarningDialogOpen,
  conflictingDates,
  setConflictingDates,
  setPendingBookingData,
  onConfirmBookingWithConflicts,
  // Availability
  availabilityDialogOpen,
  setAvailabilityDialogOpen,
  availabilitySlot,
  onAddBlock,
  onAddException,
  onRemoveBlock,
  onRemoveBlockException,
  onAddBlockAllDay,
  onAddStudioBlock,
  onRemoveStudioBlock,
  // Quick Checkout
  quickCheckoutOpen,
  setQuickCheckoutOpen,
  checkoutCustomer,
  onQuickCheckoutSuccess,
  // Anamnese
  anamneseDialogOpen,
  setAnamneseDialogOpen,
  anamneseCustomer,
  appointments,
  fetchAnamneseStatus,
  // Break
  breakDialogOpen,
  setBreakDialogOpen,
  fetchAppointments,
  // Series Skipped
  seriesSkippedInfo,
  setSeriesSkippedInfo,
}) => {
  const [historyAppointmentId, setHistoryAppointmentId] = React.useState(null);
  // 08/2026: Abrechnung zurücksetzen — pro Teilnehmer oder alle (Payrexx-Gebühr!)
  const [resetBillingApt, setResetBillingApt] = React.useState(null);
  // 08/2026: Produkt-Schnellverkauf aus dem Kalender-Kontextmenü
  const [sellProductFor, setSellProductFor] = React.useState(null);
  // 06/2026: Eigene Kundenfelder schnell aus dem Kalender bearbeiten
  const [customFieldsFor, setCustomFieldsFor] = React.useState(null);
  const resetParticipants = React.useMemo(() => {
    if (!resetBillingApt) return [];
    const ids = [...(resetBillingApt.customer_ids || [])];
    if (resetBillingApt.customer_id && !ids.includes(resetBillingApt.customer_id)) {
      ids.unshift(resetBillingApt.customer_id);
    }
    const names = resetBillingApt.customer_names || [];
    const pb = resetBillingApt.participants_billing || {};
    return ids.map((id, i) => ({
      id,
      name: names[i] || `Teilnehmer ${i + 1}`,
      billed: (pb[id] || {}).status,
    }));
  }, [resetBillingApt]);
  const doResetBilling = async (cid) => {
    try {
      const res = await axios.post(
        `${API}/appointments/${resetBillingApt.id}/reset-billing`,
        cid ? { customer_id: cid } : {}
      );
      toast.success(res.data?.message || 'Abrechnung zurückgesetzt');
      setResetBillingApt(null);
      fetchAppointments && fetchAppointments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Zurücksetzen fehlgeschlagen');
    }
  };
  // 08/2026: Versehentliches Zurücksetzen rückgängig machen
  const doUndoResetBilling = async () => {
    try {
      const res = await axios.post(
        `${API}/appointments/${resetBillingApt.id}/undo-reset-billing`, {}
      );
      toast.success(res.data?.message || 'Rückgängig gemacht');
      setResetBillingApt(null);
      fetchAppointments && fetchAppointments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rückgängig fehlgeschlagen');
    }
  };

  // 06/2026: Kalender-Rechtsklick → direkt zum Kundenprofil (Deep-Link öffnet
  // das Profil-Modal auf der Kunden-Seite via ?open=<user_id>).
  const navigate = useNavigate();
  const handleGoToProfile = async (customerId) => {
    setContextMenu(null);
    try {
      const { data } = await axios.get(`${API}/customers/${customerId}`);
      const userId = data?.user_id;
      if (userId) navigate(`/customers?open=${userId}`);
      else toast.error('Profil konnte nicht geöffnet werden');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Profil konnte nicht geöffnet werden');
    }
  };
  // 06/2026: Schnell-Notiz direkt aus dem Kalender hinzufügen (Datum, Uhrzeit
  // & Autor werden serverseitig gesetzt). Volle Verwaltung im Kundenprofil.
  const handleAddNote = async (customerId) => {
    setContextMenu(null);
    const txt = window.prompt('Notiz hinzufügen (Datum, Uhrzeit & Autor werden automatisch gespeichert):');
    if (txt === null) return;
    const text = txt.trim();
    if (!text) return;
    try {
      await axios.post(`${API}/customers/${customerId}/notes`, { text, category: 'general', visibility: 'internal' });
      toast.success('Notiz gespeichert');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Notiz konnte nicht gespeichert werden');
    }
  };
  // 06/2026: App-Link (Android + Apple) an den Kunden senden (E-Mail + WhatsApp).
  const handleSendAppLink = async (customerId) => {
    setContextMenu(null);
    try {
      const { data } = await axios.post(`${API}/customers/${customerId}/send-app-link`);
      const ch = (data?.channels || []).join(' & ') || 'kein Kanal';
      toast.success(`App-Link gesendet (${ch})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'App-Link konnte nicht gesendet werden');
    }
  };

  return (
  <>
    {/* Context Menu */}
    <AppointmentContextMenu
      contextMenu={contextMenu}
      onClose={() => setContextMenu(null)}
      appointment={selectedAppointment}
      services={services}
      trainers={trainers}
      anamneseStatus={anamneseStatus}
      menuConfig={menuConfig}
      onEdit={onEditAppointment}
      onViewAnamnese={onViewAnamnese}
      onGoToProfile={handleGoToProfile}
      onSendAppLink={handleSendAppLink}
      onAddNote={handleAddNote}
      onEditCustomFields={(customerId) => { setContextMenu(null); setCustomFieldsFor(customerId); }}
      onWhatsAppReschedule={onWhatsAppReschedule}
      onCheckIn={onCheckIn}
      onMove={onMoveAppointment}
      onMoveParticipant={onMoveParticipant}
      onRemoveParticipant={onRemoveParticipant}
      onAvailableSlotClick={onAvailableSlotClick}
      onOpenDeleteOptions={() => setDeleteOptionsDialogOpen(true)}
      onManageSeries={onManageSeries}
      onShowHistory={() => {
        if (selectedAppointment) setHistoryAppointmentId(selectedAppointment.id);
        setContextMenu(null);
      }}
      onResetBilling={() => {
        if (!selectedAppointment) return;
        setContextMenu(null);
        setResetBillingApt(selectedAppointment);
      }}
      onSellProduct={(cid, name) => {
        setContextMenu(null);
        const parts = (name || '').split(' ');
        setSellProductFor({ id: cid, first_name: parts[0] || '', last_name: parts.slice(1).join(' ') });
      }}
      onEditSuitSize={async (customerId, value) => {
        try {
          await axios.put(`${API}/customers/${customerId}/profile`, { suit_size: value });
          toast.success(value ? `EMS-Anzugsgrösse gespeichert: ${value}` : 'EMS-Anzugsgrösse geleert');
          fetchAppointments && fetchAppointments();
        } catch (e) {
          toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
        }
      }}
    />

    <AppointmentHistoryDialog
      appointmentId={historyAppointmentId}
      open={!!historyAppointmentId}
      onOpenChange={(o) => { if (!o) setHistoryAppointmentId(null); }}
    />

    {/* Produkt-Schnellverkauf aus dem Kalender (08/2026) */}
    <SellProductDialog
      open={!!sellProductFor}
      onOpenChange={(o) => { if (!o) setSellProductFor(null); }}
      customer={sellProductFor}
    />

    {/* Eigene Kundenfelder schnell bearbeiten (06/2026) */}
    <CustomFieldsQuickDialog
      customerId={customFieldsFor}
      open={!!customFieldsFor}
      onOpenChange={(o) => { if (!o) setCustomFieldsFor(null); }}
      onSaved={() => fetchAppointments && fetchAppointments()}
    />

    {/* Abrechnung zurücksetzen (08/2026) — Teilnehmer wählbar */}
    <Dialog open={!!resetBillingApt} onOpenChange={(o) => { if (!o) setResetBillingApt(null); }}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="reset-billing-dialog">
        <DialogHeader>
          <DialogTitle>Abrechnung zurücksetzen</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-600">
          Der gewählte Teilnehmer wird bei der nächsten Tages-/Nachtabrechnung
          <strong> erneut mit seinem korrekten Preis</strong> abgerechnet
          (individuelle Preise werden berücksichtigt).
        </p>
        <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
          ⚠️ Bestehende Rechnungen bleiben bestehen. Nur zurücksetzen, wer noch
          <strong> keine korrekte Rechnung</strong> hat — sonst wird doppelt verrechnet!
        </div>
        {resetBillingApt?.billing_reset_undo && (
          <div className="rounded-md bg-blue-50 border border-blue-200 p-3 flex items-center justify-between gap-2">
            <span className="text-xs text-blue-800">
              ↩️ Zuletzt zurückgesetzt
              {resetBillingApt.billing_reset_undo.reset_at
                ? ` am ${new Date(resetBillingApt.billing_reset_undo.reset_at).toLocaleString('de-CH')}`
                : ''} — versehentlich?
            </span>
            <Button size="sm" variant="outline" className="text-blue-700 border-blue-300 hover:bg-blue-100 shrink-0"
              onClick={doUndoResetBilling} data-testid="undo-reset-billing">
              Rückgängig machen
            </Button>
          </div>
        )}
        <div className="space-y-2">
          {resetParticipants.map((p) => (
            <div key={p.id} className="flex items-center justify-between border rounded-md px-3 py-2">
              <div>
                <span className="font-medium text-sm">{p.name}</span>
                <span className={`ml-2 text-xs ${p.billed ? 'text-green-600' : 'text-gray-400'}`}>
                  {p.billed ? `bereits ${p.billed === 'paid' ? 'bezahlt' : 'verrechnet'}` : 'nicht verrechnet'}
                </span>
              </div>
              <Button size="sm" variant="outline" className="text-amber-700 border-amber-300 hover:bg-amber-50"
                onClick={() => doResetBilling(p.id)}
                data-testid={`reset-billing-participant-${p.id}`}>
                Nur {p.name.split(' ')[0]} zurücksetzen
              </Button>
            </div>
          ))}
        </div>
        <div className="flex justify-between pt-2">
          <Button variant="ghost" onClick={() => setResetBillingApt(null)} data-testid="reset-billing-cancel">
            Abbrechen
          </Button>
          <Button variant="destructive" onClick={() => doResetBilling(null)} data-testid="reset-billing-all">
            Alle Teilnehmer zurücksetzen
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Edit Dialog */}
    <EditAppointmentDialog
      open={editDialogOpen}
      onOpenChange={setEditDialogOpen}
      appointment={selectedAppointment}
      services={services}
      customers={customers}
      customTime={customTime}
      setCustomTime={setCustomTime}
      bookingData={bookingData}
      setBookingData={setBookingData}
      onSubmit={onEditSubmit}
    />

    {/* Delete Confirmation Dialog */}
    <DeleteConfirmDialog
      open={deleteDialogOpen}
      onOpenChange={setDeleteDialogOpen}
      onConfirm={onConfirmDelete}
    />

    {/* Booking Dialog */}
    <BookingDialog
      open={bookingDialogOpen}
      onOpenChange={(open) => {
        setBookingDialogOpen(open);
        if (!open) {
          setIsAddingParticipant(false);
          setAvailableServices(services);
        }
      }}
      selectedTrainer={selectedTrainer}
      selectedSlot={selectedSlot}
      currentDate={currentDate}
      customTime={customTime}
      setCustomTime={setCustomTime}
      bookingData={bookingData}
      setBookingData={setBookingData}
      services={services}
      availableServices={availableServices}
      customers={customers}
      isAddingParticipant={isAddingParticipant}
      isRecurring={isRecurring}
      setIsRecurring={setIsRecurring}
      recurringFrequency={recurringFrequency}
      setRecurringFrequency={setRecurringFrequency}
      recurringOccurrences={recurringOccurrences}
      setRecurringOccurrences={setRecurringOccurrences}
      notifyCustomer={notifyCustomer}
      setNotifyCustomer={setNotifyCustomer}
      isBooking={isBooking}
      onSubmit={onBookingSubmit}
      onCancel={() => {
        setBookingDialogOpen(false);
        setIsAddingParticipant(false);
        setAvailableServices(services);
      }}
    />

    {/* Delete Options Dialog */}
    <DeleteOptionsDialog
      open={deleteOptionsDialogOpen}
      onOpenChange={setDeleteOptionsDialogOpen}
      appointment={selectedAppointment}
      services={services}
      onDelete={onDeleteAppointment}
    />

    {/* Overwrite Warning Dialog */}
    <OverwriteWarningDialog
      open={overwriteWarningDialogOpen}
      onOpenChange={setOverwriteWarningDialogOpen}
      conflictingDates={conflictingDates}
      recurringOccurrences={recurringOccurrences}
      onCancel={() => {
        setOverwriteWarningDialogOpen(false);
        setConflictingDates([]);
        setPendingBookingData(null);
      }}
      onConfirm={onConfirmBookingWithConflicts}
    />

    {/* Availability Management Dialog */}
    <AvailabilityDialog
      open={availabilityDialogOpen}
      onOpenChange={setAvailabilityDialogOpen}
      slot={availabilitySlot}
      onAddBlock={onAddBlock}
      onAddException={onAddException}
      onRemoveBlock={onRemoveBlock}
      onRemoveBlockException={onRemoveBlockException}
      onAddBlockAllDay={onAddBlockAllDay}
      onAddStudioBlock={onAddStudioBlock}
      onRemoveStudioBlock={onRemoveStudioBlock}
    />

    {/* Quick Checkout Dialog */}
    <QuickCheckoutDialog
      isOpen={quickCheckoutOpen}
      onClose={() => setQuickCheckoutOpen(false)}
      appointment={selectedAppointment}
      customer={checkoutCustomer}
      onCheckInSuccess={onQuickCheckoutSuccess}
    />

    {/* Anamnese Dialog */}
    <AnamneseDialog
      open={anamneseDialogOpen}
      onOpenChange={setAnamneseDialogOpen}
      customer={anamneseCustomer}
      onSave={() =>
        fetchAnamneseStatus(
          appointments.map((a) => a.customer_id).filter(Boolean)
        )
      }
    />

    {/* Pause eintragen Dialog */}
    <BreakDialog
      open={breakDialogOpen}
      onOpenChange={setBreakDialogOpen}
      trainers={trainers}
      currentDate={currentDate}
      onSaved={fetchAppointments}
    />

    {/* Serien-Übersprungen Dialog */}
    <SeriesSkippedDialog
      open={!!seriesSkippedInfo}
      onOpenChange={(o) => {
        if (!o) setSeriesSkippedInfo(null);
      }}
      info={seriesSkippedInfo}
    />
  </>
  );
};

export default AppointmentsDialogs;
